import { Form, object, string } from '@appliedsystems/applied-design-system';
import React from 'react';
import * as yup from 'yup';
import { usePaymentsTranslation } from '../../hooks/usePaymentsTranslation';
import { EftComponentFormFields } from './EftComponentFormFields';
import { PadAgreementCheckbox } from './PadAgreementCheckbox';

export type EftComponentFormValues = {
  accountHolderName: string;
  bankNumber: string;
  transitNumber: string;
  accountNumber: string;
  reEnterAccountNumber: string;
  storePaymentMethod: boolean;
};
type Props = {
  handleOnSubmit: (values: EftComponentFormValues) => void;
  selectedMethod: string | 'new';
  setDisablePayNowButton: React.Dispatch<React.SetStateAction<boolean>>;
  hideSavePaymentMethod?: boolean;
  children?: React.ReactNode;
};

export const EftComponentForm = ({
  handleOnSubmit,
  children,
  selectedMethod,
  hideSavePaymentMethod = true,
  setDisablePayNowButton,
}: Props) => {
  const { t } = usePaymentsTranslation();

  const formSchema = React.useMemo(() => {
    const padAgreementSchema = {
      padAgreementCheckbox: yup.boolean().oneOf([true], t('PAD_AGREEMENT_REQUIRED')).default(false),
    };

    if (selectedMethod === 'new') {
      return object({
        accountHolderName: string().required(t('ACCOUNT_HOLDER_NAME_REQUIRED')),
        bankNumber: string().required(t('BANK_NUMBER_REQUIRED')).length(3, t('BANK_NUMBER_MUST_BE_3_DIGITS')),
        transitNumber: string().required(t('TRANSIT_NUMBER_REQUIRED')).length(5, t('TRANSIT_NUMBER_MUST_BE_5_DIGITS')),
        accountNumber: string()
          .required(t('ACCOUNT_NUMBER_REQUIRED'))
          .min(7, t('ACCOUNT_NUMBER_MUST_BE_AT_LEAST_7_DIGITS'))
          .max(12, t('ACCOUNT_NUMBER_MUST_BE_AT_MOST_12_DIGITS'))
          .test('account-numbers-match', t('ACCOUNT_NUMBERS_MUST_MATCH'), function (value) {
            if (this.parent.reEnterAccountNumber) {
              return value === this.parent.reEnterAccountNumber;
            }
            return true;
          }),
        reEnterAccountNumber: string()
          .required(t('ACCOUNT_NUMBERS_MUST_MATCH'))
          .test('account-numbers-match', t('ACCOUNT_NUMBERS_MUST_MATCH'), function (value) {
            if (this.parent.accountNumber) {
              return value === this.parent.accountNumber;
            }
            return true;
          }),
        storePaymentMethod: yup.boolean().default(false),
        ...padAgreementSchema,
      });
    }

    return object(padAgreementSchema);
  }, [t, selectedMethod]);

  return (
    <Form schema={formSchema} onSubmit={handleOnSubmit}>
      <EftComponentFormFields
        selectedMethod={selectedMethod}
        hideSavePaymentMethod={hideSavePaymentMethod}
        setDisablePayNowButton={setDisablePayNowButton}
      />
      {children}
      <PadAgreementCheckbox name="padAgreementCheckbox" />
    </Form>
  );
};
